import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Divider,
  Card,
  CardContent,
  useTheme,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  CardActionArea,
} from '@mui/material';
import {
  MenuBook as MenuBookIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Quiz as QuizIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Chat as ChatIcon,
  PlaylistAdd as PlaylistAddIcon,
  Edit as EditIcon,
  ManageSearch as ManageSearchIcon,
  MergeType as MergeTypeIcon,
  Assessment as AssessmentIcon,
  Forum as ForumIcon,
  Bookmark as BookmarkIcon,
  AutoStories as AutoStoriesIcon,
  List as ListIcon,
  ContentCopy as ContentCopyIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import axios from '../../../api/axios';
import MarkdownRenderer from '../../../components/Common/MarkdownRenderer';
import Section from '../../../components/Notebook/Section';
import PageHeader from '../../../components/Common/PageHeader';
import { AuthContext } from '../../../contexts/AuthContext';
import NotebookMemoryTab from './NotebookMemoryTab';

function NotebookDetail() {
  const { notebookId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { user, selectedCourse } = useContext(AuthContext);
  const isTeacher = user?.role === 'teacher';
  const tabFromUrl = searchParams.get('tab') === 'skills' ? 1 : searchParams.get('tab') === 'memory' ? 2 : 0;
  const [tabValue, setTabValue] = useState(tabFromUrl);
  const [notebookData, setNotebookData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [statistics, setStatistics] = useState({ total: 0, completed: 0 });
  const [createSectionDialogOpen, setCreateSectionDialogOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [creatingSection, setCreatingSection] = useState(false);
  const [createSectionError, setCreateSectionError] = useState('');
  const [outlineDialogOpen, setOutlineDialogOpen] = useState(false);

  // 同步 URL ?tab=skills | ?tab=memory 与 tab 状态
  useEffect(() => {
    const tab = searchParams.get('tab');
    setTabValue(tab === 'skills' ? 1 : tab === 'memory' ? 2 : 0);
  }, [searchParams]);

  useEffect(() => {
    fetchNotebook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notebookId]);

  useEffect(() => {
    if (notebookData) {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notebookData, selectedCourse]);

  const fetchNotebook = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/notes/${notebookId}`);
      setNotebookData(response.data);
    } catch (error) {
      console.error('Error fetching notebook:', error);
      setError('加载笔记失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async (sectionTitle) => {
    if (!window.confirm(`确定要删除章节"${sectionTitle}"吗？这将删除该章节的所有知识和练习题，删除后无法恢复。`)) {
      return;
    }

    try {
      await axios.delete(`/notes/${notebookId}/section/${encodeURIComponent(sectionTitle)}`);
      // Refresh notebook data
      await fetchNotebook();
    } catch (error) {
      console.error('Error deleting section:', error);
      alert('删除失败：' + (error.response?.data?.error || '请重试'));
    }
  };

  const fetchStatistics = async () => {
    try {
      if (!selectedCourse?.id || !notebookData) {
        // If no course selected, just get total count
        const response = await axios.get(`/notebook-examples/notebook/${notebookId}`);
        const total = response.data.examples?.length || 0;
        setStatistics({ total, completed: 0 });
        return;
      }

      const courseTemplateId = selectedCourse.id;
      const categoriesRes = await axios.get('/questions/categories', {
        params: { courseTemplateId: parseInt(courseTemplateId) }
      });

      const notebookTitle = notebookData?.outline?.notebook_title;
      const notebookCategory = categoriesRes.data.find(cat => cat.category === notebookTitle);
      
      if (notebookCategory) {
        setStatistics({
          total: notebookCategory.count || 0,
          completed: notebookCategory.completed || 0
        });
      } else {
        // Fallback: get total count from examples
        const response = await axios.get(`/notebook-examples/notebook/${notebookId}`);
        const total = response.data.examples?.length || 0;
        setStatistics({ total, completed: 0 });
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
      // Fallback: get total count from examples
      try {
        const response = await axios.get(`/notebook-examples/notebook/${notebookId}`);
        const total = response.data.examples?.length || 0;
        setStatistics({ total, completed: 0 });
      } catch (e) {
        setStatistics({ total: 0, completed: 0 });
      }
    }
  };

  const handleBack = () => {
    navigate('/notebooks');
  };

  const handleOpenCreateSectionDialog = () => {
    setCreateSectionDialogOpen(true);
    setNewSectionTitle('');
    setCreateSectionError('');
  };

  const handleCloseCreateSectionDialog = () => {
    setCreateSectionDialogOpen(false);
    setNewSectionTitle('');
    setCreateSectionError('');
  };

  const handleCreateSection = async () => {
    if (!newSectionTitle.trim()) {
      setCreateSectionError('请输入章节标题');
      return;
    }

    setCreatingSection(true);
    setCreateSectionError('');

    try {
      await axios.post(`/notes/${notebookId}/section`, {
        sectionTitle: newSectionTitle.trim()
      });

      // Refresh notebook data
      await fetchNotebook();
      handleCloseCreateSectionDialog();
    } catch (error) {
      console.error('Error creating section:', error);
      setCreateSectionError(
        error.response?.data?.error || 
        '创建章节失败，请重试'
      );
    } finally {
      setCreatingSection(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <PageHeader
          title="加载中..."
          backPath={handleBack}
          showBackButton={false}
        />
        <Container sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 8, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            加载中...
          </Typography>
        </Container>
      </Box>
    );
  }

  if (error || !notebookData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <PageHeader
          title="笔记详情"
          backPath={handleBack}
        />
        <Container sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="error">
              {error || '笔记不存在'}
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  const { outline, sections } = notebookData;
  
  // Filter out metadata fields that are not actual sections
  // A real section should have a 'section_title' property
  const metadataFields = ['id', 'section_title_id', 'introduction_id', 'summary_id', 
                          'definition_id', 'question_id', 'answer_id', 'explanation_id', 
                          'proof_id', 'theorem_id'];
  const sectionTitles = Object.keys(sections).filter(key => {
    // Skip metadata fields
    if (metadataFields.includes(key)) {
      return false;
    }
    // Only include keys that have a section object with section_title property
    const section = sections[key];
    return section && typeof section === 'object' && section.section_title;
  });

  // Generate poster image URL based on notebook title
  const getPosterImage = (title) => {
    return `https://picsum.photos/seed/${encodeURIComponent(title)}/1200/400`;
  };

  const posterImageUrl = getPosterImage(outline.notebook_title);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1) {
      setSearchParams({ tab: 'skills' });
    } else if (newValue === 2) {
      setSearchParams({ tab: 'memory' });
    } else {
      setSearchParams({});
    }
  };

  return (
    <Box sx={{ width: '100%', pb: 4 }}>
      <PageHeader
        title={outline.notebook_title}
        backPath={handleBack}
        actions={
          <Button
            variant="outlined"
            startIcon={<QuizIcon />}
            onClick={() => navigate(`/questions/category/${encodeURIComponent(outline.notebook_title)}`)}
            size="small"
          >
            查看例题
          </Button>
        }
      />

      <Container sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, mt: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
        >
          <Tab label="查看笔记" id="notebook-tab-0" aria-controls="notebook-tabpanel-0" />
          <Tab label="技能" id="notebook-tab-1" aria-controls="notebook-tabpanel-1" />
          <Tab label="记忆" id="notebook-tab-2" aria-controls="notebook-tabpanel-2" />
        </Tabs>

        {/* Tab 0: 查看笔记 */}
        <Box role="tabpanel" id="notebook-tabpanel-0" aria-labelledby="notebook-tab-0" hidden={tabValue !== 0}>
          <Box>
          {/* Poster 区域 */}
          <Box sx={{ mb: 4 }}>
            <Paper
              sx={{
                position: 'relative',
                backgroundColor: 'grey.800',
                color: '#fff',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundImage: `url(${posterImageUrl})`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <img 
                style={{ display: 'none' }} 
                src={posterImageUrl} 
                alt={outline.notebook_title} 
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  left: 0,
                  backgroundColor: 'rgba(0,0,0,.4)',
                }}
              />
              <Box
                sx={{
                  position: 'relative',
                  p: { xs: 3, md: 4 },
                }}
              >
                <Grid container spacing={4}>
                  {/* 左侧：简介 */}
                  {outline.notebook_description && (
                    <Grid item xs={12} md={8}>
                      <Typography
                        component="h2"
                        variant="h5"
                        color="inherit"
                        gutterBottom
                        fontWeight="bold"
                        sx={{ mb: 2 }}
                      >
                        简介
                      </Typography>
                      <Box
                        sx={{
                          color: 'inherit',
                          '& p': {
                            color: 'inherit',
                            opacity: 0.95,
                            mb: 1,
                          },
                        }}
                      >
                        <MarkdownRenderer content={outline.notebook_description} />
                      </Box>
                    </Grid>
                  )}

                  {/* 右侧：统计信息 */}
                  <Grid item xs={12} md={4}>
                    <Typography
                      component="h2"
                      variant="h5"
                      color="inherit"
                      gutterBottom
                      fontWeight="bold"
                      sx={{ mb: 2 }}
                    >
                      练习题统计
                    </Typography>
                    <Box>
                      <Typography
                        variant="body1"
                        color="inherit"
                        sx={{ opacity: 0.95, mb: 1 }}
                      >
                        总题数
                      </Typography>
                      <Typography
                        variant="h4"
                        color="inherit"
                        fontWeight="bold"
                        sx={{ mb: 3 }}
                      >
                        {statistics.total}
                      </Typography>
                      {!isTeacher && statistics.completed > 0 && (
                        <>
                          <Typography
                            variant="body1"
                            color="inherit"
                            sx={{ opacity: 0.95, mb: 1 }}
                          >
                            已完成
                          </Typography>
                          <Typography
                            variant="h4"
                            color="inherit"
                            fontWeight="bold"
                          >
                            {statistics.completed} / {statistics.total}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* 笔记内容 */}
          <Box>

            {/* 笔记内容 */}
            {sectionTitles.length > 0 ? (
                <Box>
                  {sectionTitles.map((title, index) => {
                    const section = sections[title];
                    if (!section) {
                      return null;
                    }

                    const isExpanded = expandedSections[section.id || title] || false;

                    return (
                      <Card
                        key={title}
                        id={`section-${title}`}
                        sx={{
                          mb: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          boxShadow: 1,
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: 2,
                          },
                        }}
                      >
                        <CardContent sx={{ p: 1.5 }}>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 2,
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' 
                                  ? 'rgba(255, 255, 255, 0.05)' 
                                  : 'rgba(0, 0, 0, 0.02)',
                              },
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              transition: 'background-color 0.2s',
                            }}
                            onClick={() => setExpandedSections((prev) => ({
                              ...prev,
                              [section.id || title]: !prev[section.id || title],
                            }))}
                          >
                            <MenuBookIcon color="secondary" sx={{ mr: 1, fontSize: 20 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, flex: 1, fontSize: '1.25rem' }}>
                              {index + 1}. {title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              {isTeacher && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteSection(title);
                                  }}
                                  sx={{
                                    '&:hover': {
                                      backgroundColor: 'error.light',
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </Box>

                          {isExpanded && (
                            <Box onClick={(e) => e.stopPropagation()}>
                              <Section 
                                section={section} 
                                title={title} 
                                order={index + 1} 
                                sectionTitle={title} 
                                hideTitle={true}
                                notebookId={notebookId}
                                isTeacher={isTeacher}
                                onSectionUpdate={fetchNotebook}
                              />
                            </Box>
                          )}

                          {!isExpanded && (
                            <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                点击上方标题栏展开查看内容
                                {section.introduction && ' • 包含章节介绍'}
                                {section.concept_blocks && section.concept_blocks.length > 0 && ' • 包含概念定义'}
                                {section.summary && ' • 包含总结'}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                  
                  {/* Add Section Button (Teacher Only) */}
                  {isTeacher && (
                    <Card
                      sx={{
                        mb: 2,
                        border: `2px dashed ${theme.palette.divider}`,
                        borderRadius: 2,
                        boxShadow: 0,
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(25, 118, 210, 0.1)' 
                            : 'rgba(25, 118, 210, 0.05)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3, textAlign: 'center' }}>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={handleOpenCreateSectionDialog}
                          sx={{ textTransform: 'none' }}
                        >
                          添加新章节
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              ) : (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    暂无内容
                  </Typography>
                  {isTeacher && (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenCreateSectionDialog}
                    >
                      新建章节
                    </Button>
                  )}
                </Paper>
              )}
          </Box>
          </Box>
        </Box>

        {/* Tab 1: 技能 - 卡片布局 */}
        <Box role="tabpanel" id="notebook-tabpanel-1" aria-labelledby="notebook-tab-1" hidden={tabValue !== 1}>
          <Typography variant="subtitle1" fontWeight="600" color="text.secondary" sx={{ mb: 2 }}>
            该笔记本支持以下技能，点击卡片进入对应功能：
          </Typography>

          {/* 通用：解答问题、获取大纲（学生与教师均可见） */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea onClick={() => navigate(`/notebook/${notebookId}/chat`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                    <ChatIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="600">解答问题</Typography>
                      <Typography variant="body2" color="text.secondary">与 AI 对话解答与笔记相关的问题</Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea onClick={() => setOutlineDialogOpen(true)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                    <ListIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="600">获取大纲</Typography>
                      <Typography variant="body2" color="text.secondary">查看本笔记本的章节大纲与描述</Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>

          {/* 学生专用 */}
          {!isTeacher && (
            <>
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">学生专用</Typography>
              </Divider>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate(`/notebook/${notebookId}/review`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <AutoStoriesIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">复习的知识点</Typography>
                          <Typography variant="body2" color="text.secondary">根据错题与未做章节针对性选题，开始复习</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate(`/notebook/${notebookId}/learning-state`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <AssessmentIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">分析学习状态</Typography>
                          <Typography variant="body2" color="text.secondary">查看本笔记下的进度、掌握、参与、求助等五维学习状态与图表</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate('/forum')} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <ForumIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">发帖</Typography>
                          <Typography variant="body2" color="text.secondary">在课程讨论区发起与本笔记相关的问题或讨论</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate(`/bookmarks?notebook=${encodeURIComponent(notebookId)}`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <BookmarkIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">查看本笔记错题本</Typography>
                          <Typography variant="body2" color="text.secondary">只看本笔记下收藏的题目，集中复习错题</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate(`/questions/notebook/${notebookId}`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <PlaylistAddIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">生成更多题目</Typography>
                          <Typography variant="body2" color="text.secondary">查看并练习本笔记下的全部题目；需要更多题目可联系教师</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}

          {/* 教师专用 */}
          {isTeacher && (
            <>
              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">教师专用</Typography>
              </Divider>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea
                      onClick={() => navigate(`/notebook/${notebookId}/chat`, {
                        state: {
                          autoSend: '请根据本笔记本的标题和大纲，生成一段 50-100 字的描述，概括该笔记本的核心内容和学习要点。只输出描述文字，不要其他说明。',
                        },
                      })}
                      sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <DescriptionIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">生成描述</Typography>
                          <Typography variant="body2" color="text.secondary">AI 生成本笔记本 50-100 字的描述，概括核心内容与学习要点</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate(`/questions/notebook/${notebookId}`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <AddIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">添加一道题目</Typography>
                          <Typography variant="body2" color="text.secondary">需指定笔记来源（如章节），基于该内容出题</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate(`/notebook/${notebookId}/add-exercises`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <PlaylistAddIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">添加多道题目</Typography>
                          <Typography variant="body2" color="text.secondary">选择章节，自动选择题型，生成结构化题目并写入对应章节</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate(`/questions/notebook/${notebookId}`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <QuizIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">生成题目</Typography>
                          <Typography variant="body2" color="text.secondary">由 AI 根据笔记整体生成题目，无需指定来源</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea
                      onClick={() => { setTabValue(0); setSearchParams({}); }}
                      sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <EditIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">编辑笔记</Typography>
                          <Typography variant="body2" color="text.secondary">编辑笔记本的章节结构与内容</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate(`/questions/notebook/${notebookId}`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <ManageSearchIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">管理题目</Typography>
                          <Typography variant="body2" color="text.secondary">查看、编辑、删除该笔记下的题目</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate(`/questions/notebook/${notebookId}`)} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <MergeTypeIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">题目去重</Typography>
                          <Typography variant="body2" color="text.secondary">检测并合并重复或相似题目</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate('/dashboard')} sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
                        <AssessmentIcon color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle1" fontWeight="600">班级情况</Typography>
                          <Typography variant="body2" color="text.secondary">掌握该笔记下学生的作答与完成情况</Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </Box>

        {/* Tab 2: 记忆 - 本笔记本下的对话与做题记录 */}
        <Box role="tabpanel" id="notebook-tabpanel-2" aria-labelledby="notebook-tab-2" hidden={tabValue !== 2}>
          <NotebookMemoryTab notebookId={notebookId} selectedCourse={selectedCourse} />
        </Box>
      </Container>

      {/* Create Section Dialog (Teacher Only) */}
      {isTeacher && (
        <Dialog 
          open={createSectionDialogOpen} 
          onClose={handleCloseCreateSectionDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            新建章节
          </DialogTitle>
          <DialogContent>
            {createSectionError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createSectionError}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="章节标题 *"
              value={newSectionTitle}
              onChange={(e) => setNewSectionTitle(e.target.value)}
              margin="normal"
              placeholder="例如: 第一章 数据结构基础"
              required
              autoFocus
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateSectionDialog}>
              取消
            </Button>
            <Button
              onClick={handleCreateSection}
              variant="contained"
              disabled={!newSectionTitle.trim() || creatingSection}
            >
              {creatingSection ? '创建中...' : '创建章节'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* 获取大纲对话框（学生与教师均可见）；仅展示标题、简介、章节，过滤内部 id 等元数据 */}
      <Dialog
        open={outlineDialogOpen}
        onClose={() => setOutlineDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          本笔记本大纲
        </DialogTitle>
        <DialogContent>
          {notebookData?.outline ? (
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                以下仅包含标题、简介与章节列表，不含内部标识。
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                标题
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {notebookData.outline.notebook_title || '（无）'}
              </Typography>
              {notebookData.outline.notebook_description && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    简介
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <MarkdownRenderer content={notebookData.outline.notebook_description} />
                  </Box>
                </>
              )}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                章节
              </Typography>
              {(() => {
                const outlineMetaKeys = new Set(['id', 'section_title_id', 'introduction_id', 'summary_id', 'definition_id', 'question_id', 'answer_id', 'explanation_id', 'proof_id', 'theorem_id']);
                const entries = notebookData.outline.outlines && typeof notebookData.outline.outlines === 'object'
                  ? Object.entries(notebookData.outline.outlines).filter(
                    ([key, val]) => !outlineMetaKeys.has(key) && typeof val === 'string'
                  )
                  : [];
                return entries.length > 0 ? (
                  <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1.5 } }}>
                    {entries.map(([title, desc], idx) => (
                      <li key={idx}>
                        <Typography variant="body2" fontWeight={500}>{title}</Typography>
                        {desc && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {desc}
                          </Typography>
                        )}
                      </li>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">暂无章节</Typography>
                );
              })()}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">暂无大纲数据</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={() => {
              if (!notebookData?.outline) return;
              const outlineMetaKeys = new Set(['id', 'section_title_id', 'introduction_id', 'summary_id', 'definition_id', 'question_id', 'answer_id', 'explanation_id', 'proof_id', 'theorem_id']);
              const entries = notebookData.outline.outlines && typeof notebookData.outline.outlines === 'object'
                ? Object.entries(notebookData.outline.outlines).filter(
                  ([key, val]) => !outlineMetaKeys.has(key) && typeof val === 'string'
                )
                : [];
              const lines = [
                `# ${notebookData.outline.notebook_title || ''}`,
                notebookData.outline.notebook_description ? `\n${notebookData.outline.notebook_description}\n` : '',
                '\n## 章节\n',
                ...entries.map(([t, d]) => `- **${t}**${d ? `\n  ${d}` : ''}`),
              ];
              navigator.clipboard?.writeText(lines.join('')).then(() => {}).catch(() => {});
            }}
            disabled={!notebookData?.outline}
          >
            复制大纲
          </Button>
          <Button onClick={() => setOutlineDialogOpen(false)}>关闭</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default NotebookDetail;
